/**
 * CSRF Token Utilities for Life Itself Studio Frontend
 *
 * Provides automatic CSRF token handling for all AJAX requests:
 * - Reads token from cookie
 * - Automatically includes token in request headers
 * - Handles token refresh on 403 responses
 */

// ============================================================================
// CONSTANTS
// ============================================================================

const CSRF_COOKIE_NAME = "csrf_token";
const CSRF_HEADER_NAME = "x-csrf-token";
const CSRF_ENDPOINT = "/api/csrf-token";

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

/**
 * Read the CSRF token from the cookie
 */
export function getCsrfTokenFromCookie(): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === CSRF_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

/**
 * Get the current CSRF token
 * First tries to read from cookie, then fetches from server if needed
 */
export async function getCsrfToken(): Promise<string | null> {
  // Try to get from cookie first
  const cookieToken = getCsrfTokenFromCookie();
  if (cookieToken) {
    return cookieToken;
  }

  // Fetch from server if not in cookie
  try {
    const response = await fetch(CSRF_ENDPOINT, {
      method: "GET",
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();
      return data.csrfToken || null;
    }
  } catch (error) {
    console.error("Failed to fetch CSRF token:", error);
  }

  return null;
}

/**
 * Refresh the CSRF token from the server
 * Call this if a request fails with 403 due to invalid token
 */
export async function refreshCsrfToken(): Promise<string | null> {
  try {
    const response = await fetch(CSRF_ENDPOINT, {
      method: "GET",
      credentials: "include",
    });

    if (response.ok) {
      const data = await response.json();
      return data.csrfToken || null;
    }
  } catch (error) {
    console.error("Failed to refresh CSRF token:", error);
  }

  return null;
}

// ============================================================================
// FETCH WRAPPER
// ============================================================================

/**
 * Enhanced fetch that automatically includes CSRF token
 * Use this for all state-changing requests (POST, PUT, PATCH, DELETE)
 */
export async function csrfFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const method = (options.method || "GET").toUpperCase();

  // Only add CSRF token for state-changing methods
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const token = getCsrfTokenFromCookie();

    if (token) {
      const headers = new Headers(options.headers);
      headers.set(CSRF_HEADER_NAME, token);
      options.headers = headers;
    }
  }

  // Ensure credentials are included for session cookies
  options.credentials = options.credentials || "include";

  let response = await fetch(url, options);

  // If we get a 403 with CSRF error, try refreshing the token and retry once
  if (response.status === 403) {
    try {
      const errorData = await response.clone().json();
      if (errorData.error && errorData.error.includes("CSRF")) {
        const newToken = await refreshCsrfToken();
        if (newToken) {
          const headers = new Headers(options.headers);
          headers.set(CSRF_HEADER_NAME, newToken);
          options.headers = headers;
          response = await fetch(url, options);
        }
      }
    } catch {
      // Ignore JSON parse errors, return original response
    }
  }

  return response;
}

// ============================================================================
// REQUEST HELPERS
// ============================================================================

/**
 * Convert headers to a plain object
 */
function headersToObject(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  if (Array.isArray(headers)) {
    const result: Record<string, string> = {};
    for (const [key, value] of headers) {
      result[key] = value;
    }
    return result;
  }
  return headers as Record<string, string>;
}

/**
 * Make a CSRF-protected POST request
 */
export async function csrfPost(
  url: string,
  data?: unknown,
  options: RequestInit = {}
): Promise<Response> {
  return csrfFetch(url, {
    ...options,
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headersToObject(options.headers),
    },
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * Make a CSRF-protected PUT request
 */
export async function csrfPut(
  url: string,
  data?: unknown,
  options: RequestInit = {}
): Promise<Response> {
  return csrfFetch(url, {
    ...options,
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...headersToObject(options.headers),
    },
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * Make a CSRF-protected PATCH request
 */
export async function csrfPatch(
  url: string,
  data?: unknown,
  options: RequestInit = {}
): Promise<Response> {
  return csrfFetch(url, {
    ...options,
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...headersToObject(options.headers),
    },
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * Make a CSRF-protected DELETE request
 */
export async function csrfDelete(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  return csrfFetch(url, {
    ...options,
    method: "DELETE",
  });
}

// ============================================================================
// FORM HELPERS
// ============================================================================

/**
 * Add CSRF token to a form as a hidden field
 */
export function addCsrfToForm(form: HTMLFormElement): void {
  const token = getCsrfTokenFromCookie();
  if (!token) {
    console.warn("No CSRF token available for form");
    return;
  }

  // Remove existing CSRF field if present
  const existingField = form.querySelector('input[name="_csrf"]');
  if (existingField) {
    existingField.remove();
  }

  // Add new hidden field
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "_csrf";
  input.value = token;
  form.appendChild(input);
}

/**
 * Create a hidden CSRF input element
 */
export function createCsrfInput(): HTMLInputElement {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "_csrf";
  input.value = getCsrfTokenFromCookie() || "";
  return input;
}

// ============================================================================
// REACT HOOK INTEGRATION
// ============================================================================

/**
 * Get CSRF headers for use with fetch or axios
 * Returns an object suitable for spreading into headers
 */
export function getCsrfHeaders(): Record<string, string> {
  const token = getCsrfTokenFromCookie();
  if (!token) {
    return {};
  }
  return {
    [CSRF_HEADER_NAME]: token,
  };
}

/**
 * Initialize CSRF protection
 * Call this early in your app to ensure token is available
 */
export async function initializeCsrf(): Promise<void> {
  const token = getCsrfTokenFromCookie();
  if (!token) {
    await refreshCsrfToken();
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export { CSRF_COOKIE_NAME, CSRF_HEADER_NAME };
